import { useMemo, useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { logFieldCalendarCrash } from "./fieldCalendarDiagnostics";
import {
  DISPATCH_END_HOUR,
  DISPATCH_PX_PER_HOUR,
  DISPATCH_ROW_HEIGHT,
  DISPATCH_START_HOUR,
  DISPATCH_TIMELINE_WIDTH,
  buildDateStrip,
  buildFieldStaffScheduleRows,
  coerceValidDay,
  dateKey,
  dispatchLeftPx,
  dispatchWidthPx,
  formatMinutesLabel,
  getVisitLane,
  groupVisitsByTechnician,
  isSameDay,
  layoutTimedVisits,
  leaveBlocksForDate,
  sanitizeVisits,
  startOfDay,
  visitPlacementDate,
  type FieldStaffRosterMember,
  type FieldStaffScheduleRow,
} from "./fieldCalendarUtils";
import { blockClassForVisit } from "./visitBlockStyles";
import type { CalendarLeaveBlock, CalendarVisit } from "./fieldCalendarTypes";
import {
  HIDE_EMPTY_TECHNICIANS_LABEL,
  SHOW_EMPTY_TECHNICIANS_LABEL,
  presentTodayTechnicianRows,
} from "./todayTechnicianPresentation";

type Props = {
  day: Date;
  visits: CalendarVisit[] | null | undefined;
  leaveBlocks: CalendarLeaveBlock[] | null | undefined;
  fieldStaff?: FieldStaffRosterMember[] | null;
  myScheduleUserId?: number;
  onVisitClick: (id: number) => void;
  onSelectDay: (day: Date) => void;
};

function initials(name: string | null | undefined): string {
  const safe = (name ?? "??").trim() || "??";
  return safe
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

export function FieldCalendarDispatchBoard({
  day,
  visits,
  leaveBlocks,
  fieldStaff,
  myScheduleUserId,
  onVisitClick,
  onSelectDay,
}: Props) {
  const [clock, setClock] = useState(() => new Date());
  const [showEmptyTechnicians, setShowEmptyTechnicians] = useState(false);
  const today = useMemo(() => startOfDay(new Date()), []);
  const safeDay = coerceValidDay(day);
  const isSingaporeToday = isSameDay(safeDay, today);

  useEffect(() => {
    if (!isSingaporeToday) setShowEmptyTechnicians(false);
  }, [isSingaporeToday]);

  const dateStrip = useMemo(() => {
    try {
      return buildDateStrip(safeDay, 14);
    } catch (error) {
      logFieldCalendarCrash("dispatchBoard.dateStrip", error);
      return [safeDay];
    }
  }, [safeDay]);

  useEffect(() => {
    const id = window.setInterval(() => setClock(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const dayKey = dateKey(safeDay);
  const dayVisits = useMemo(() => {
    try {
      return sanitizeVisits(visits).filter(
        (v) => dateKey(visitPlacementDate(v)) === dayKey,
      );
    } catch (error) {
      logFieldCalendarCrash("dispatchBoard.dayVisits", error);
      return [];
    }
  }, [visits, dayKey]);

  const techRows = useMemo(() => {
    try {
      return groupVisitsByTechnician(dayVisits);
    } catch (error) {
      logFieldCalendarCrash("dispatchBoard.techRows", error);
      return [];
    }
  }, [dayVisits]);

  const dayLeave = leaveBlocksForDate(leaveBlocks, safeDay, myScheduleUserId);

  const todayRosterRows = useMemo(() => {
    if (!isSingaporeToday) return null;
    const roster = Array.isArray(fieldStaff) ? fieldStaff : [];
    if (roster.length === 0) return null;
    try {
      return buildFieldStaffScheduleRows(dayVisits, dayLeave, roster);
    } catch (error) {
      logFieldCalendarCrash("dispatchBoard.todayRoster", error);
      return null;
    }
  }, [isSingaporeToday, fieldStaff, dayVisits, dayLeave]);

  const todayPresentation = useMemo(() => {
    if (!todayRosterRows) return null;
    return presentTodayTechnicianRows(todayRosterRows, {
      applyTodayPresentation: true,
      showEmptyTechnicians,
    });
  }, [todayRosterRows, showEmptyTechnicians]);

  const hours = Array.from(
    { length: DISPATCH_END_HOUR - DISPATCH_START_HOUR + 1 },
    (_, i) => DISPATCH_START_HOUR + i,
  );

  const nowLinePx = useMemo(() => {
    try {
      if (!isSameDay(safeDay, clock)) return null;
      const mins = clock.getHours() * 60 + clock.getMinutes();
      const origin = DISPATCH_START_HOUR * 60;
      const end = DISPATCH_END_HOUR * 60;
      if (mins < origin || mins > end) return null;
      return dispatchLeftPx(mins);
    } catch (error) {
      logFieldCalendarCrash("dispatchBoard.nowLine", error);
      return null;
    }
  }, [safeDay, clock]);

  const timelineWidth = Number.isFinite(DISPATCH_TIMELINE_WIDTH)
    ? DISPATCH_TIMELINE_WIDTH
    : 792;

  return (
    <div className="bg-white">
      <div className="flex gap-0 overflow-x-auto border-b border-slate-200 px-2 py-2 scrollbar-none">
        {dateStrip.map((d) => {
          const selected = isSameDay(d, safeDay);
          const isToday = isSameDay(d, today);
          return (
            <button
              key={dateKey(d)}
              type="button"
              onClick={() => onSelectDay(d)}
              className={cn(
                "flex min-w-[44px] shrink-0 flex-col items-center rounded-lg px-1.5 py-1",
                selected && "bg-blue-600 text-white",
                !selected && "text-slate-700",
              )}
            >
              <span className="text-[9px] font-bold uppercase">
                {d.toLocaleDateString("en-SG", { weekday: "short" }).slice(0, 3)}
              </span>
              <span
                className={cn(
                  "mt-0.5 flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold",
                  isToday && !selected && "ring-2 ring-blue-500",
                )}
              >
                {d.getDate()}
              </span>
            </button>
          );
        })}
      </div>

      {todayPresentation?.showToggle ? (
        <div className="flex items-center justify-end border-b border-slate-100 px-2 py-1.5">
          <button
            type="button"
            aria-pressed={showEmptyTechnicians}
            aria-expanded={showEmptyTechnicians}
            onClick={() => setShowEmptyTechnicians((v) => !v)}
            className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-bold text-slate-700"
          >
            {showEmptyTechnicians ? HIDE_EMPTY_TECHNICIANS_LABEL : SHOW_EMPTY_TECHNICIANS_LABEL}
          </button>
        </div>
      ) : null}

      <div className="overflow-x-auto overscroll-x-contain border-b border-slate-200">
        <div className="relative min-w-max" style={{ width: timelineWidth + 120 }}>
          <div
            className="sticky top-0 z-10 grid border-b border-slate-200 bg-slate-50"
            style={{ gridTemplateColumns: "112px 1fr" }}
          >
            <div className="border-r border-slate-200 px-2 py-1 text-[10px] font-bold text-slate-500">
              Technician
            </div>
            <div className="relative flex" style={{ width: timelineWidth }}>
              {hours.map((h) => {
                const ampm = h < 12 ? "AM" : "PM";
                const h12 = h % 12 === 0 ? 12 : h % 12;
                return (
                  <div
                    key={h}
                    className="shrink-0 border-r border-slate-100 px-1 py-1 text-[10px] font-semibold text-slate-500"
                    style={{ width: DISPATCH_PX_PER_HOUR }}
                  >
                    {h12} {ampm}
                  </div>
                );
              })}
            </div>
          </div>

          {todayPresentation
            ? todayPresentation.visibleRows.length === 0 && (
                <p className="px-4 py-8 text-center text-sm text-slate-500">No dispatch assignments</p>
              )
            : techRows.length === 0 &&
              dayLeave.length === 0 && (
                <p className="px-4 py-8 text-center text-sm text-slate-500">No dispatch assignments</p>
              )}

          {(todayPresentation ? todayPresentation.visibleRows : techRows).map((row) => {
            const scheduleRow = row as FieldStaffScheduleRow;
            const rowVisits = Array.isArray(row.visits) ? row.visits : [];
            const leaveOnly =
              Boolean(todayPresentation) &&
              rowVisits.length === 0 &&
              scheduleRow.onLeave != null;
            if (leaveOnly) {
              const lb = scheduleRow.onLeave!;
              return (
                <div
                  key={lb.id ?? `leave-${lb.userId}`}
                  className="grid border-b border-slate-100 bg-sky-50/80"
                  style={{ gridTemplateColumns: "112px 1fr" }}
                >
                  <div className="px-2 py-2 text-xs font-bold text-sky-900">
                    {lb.userName ?? "Leave"}
                  </div>
                  <div className="px-3 py-2 text-xs font-semibold text-sky-800">On leave</div>
                </div>
              );
            }

            const layouts = layoutTimedVisits(rowVisits);
            const rowSpan = Math.max(
              DISPATCH_ROW_HEIGHT,
              layouts.reduce((max, l) => Math.max(max, (l.column + 1) * 44), DISPATCH_ROW_HEIGHT),
            );
            const rowKey = row.userId || row.userName || "row";

            return (
              <div
                key={rowKey}
                className="grid border-b border-slate-100"
                style={{ gridTemplateColumns: "112px 1fr" }}
              >
                <div className="flex items-center gap-2 border-r border-slate-200 px-2 py-2">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-700">
                    {initials(row.userName)}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-xs font-bold text-slate-900">
                      {row.userName ?? "Technician"}
                    </p>
                    <p className="truncate text-[10px] capitalize text-slate-500">
                      {row.role ?? "technician"}
                    </p>
                  </div>
                </div>
                <div
                  className="relative"
                  style={{ width: timelineWidth, minHeight: rowSpan }}
                >
                  {nowLinePx != null && (
                    <div
                      className="pointer-events-none absolute top-0 bottom-0 z-20 w-0.5 bg-blue-600"
                      style={{ left: nowLinePx }}
                    />
                  )}
                  {layouts.map(({ visit, startMins, endMins, column }) => {
                    if (!visit?.id) return null;
                    const lane = getVisitLane(visit);
                    const left = dispatchLeftPx(startMins);
                    const width = Math.max(dispatchWidthPx(startMins, endMins), 64);
                    const top = 6 + column * 44;
                    const site =
                      visit.customerName?.trim() || visit.customerAddress?.trim() || "—";

                    return (
                      <button
                        key={visit.id}
                        type="button"
                        onClick={() => onVisitClick(visit.id)}
                        className={cn(
                          "absolute z-10 overflow-hidden rounded-md px-2 py-1 text-left shadow-sm active:opacity-90",
                          blockClassForVisit(lane, undefined, "dispatch"),
                        )}
                        style={{
                          left,
                          width,
                          top,
                          maxWidth: Math.max(64, timelineWidth - left - 4),
                        }}
                      >
                        <p className="truncate text-xs font-bold leading-tight">{site}</p>
                        <p className="truncate text-[10px] font-medium opacity-90">
                          {formatMinutesLabel(startMins)} – {formatMinutesLabel(endMins)}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {!todayPresentation &&
            dayLeave.map((lb) => (
              <div
                key={lb.id ?? `leave-${lb.userId}`}
                className="grid border-b border-slate-100 bg-sky-50/80"
                style={{ gridTemplateColumns: "112px 1fr" }}
              >
                <div className="px-2 py-2 text-xs font-bold text-sky-900">
                  {lb.userName ?? "Leave"}
                </div>
                <div className="px-3 py-2 text-xs font-semibold text-sky-800">On leave</div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
